import { format, isValid, parse } from 'date-fns'
import { cs } from 'date-fns/locale'
import dynamic from 'next/dynamic'
import { Controller } from 'react-hook-form'

import * as Popover from '@radix-ui/react-popover'

import { TCommonFields } from '../../types'
import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'

const DateRangePicker = dynamic(
    () => import('react-date-range').then(mod => mod.DateRangePicker),
    { ssr: false }
)

const DATE_FORMAT = 'dd.MM.yyyy'

export type TDateRangeField<T> = TCommonFields<T> & {
    type: 'date-range'
    default?: [Date, Date]
    loadDefault?: () => Promise<[Date, Date]>
    /** earliest selectable day; any day when not set */
    minDate?: Date
}

/** Parses a `dd.MM.yyyy` string; anything else gives `undefined` instead of an Invalid Date. */
const parseDate = (value: unknown) => {
    if (typeof value !== 'string')
        return undefined

    const date = parse(value, DATE_FORMAT, new Date())
    return isValid(date) ? date : undefined
}

type TProps<T> = {
    rowField: TDateRangeField<T>
    fieldName: string
    labelWidth?: string
}

const DateRangeField = <T,>({ rowField, fieldName, labelWidth }: TProps<T>) => {
    const { disabled, required } = useFieldState(rowField)

    return (
        <FieldRow
            fieldName={fieldName}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className="form-date-range-input"
        >
            <Controller
                name={fieldName}
                rules={getFieldRules(rowField, required)}
                render={({ field }) => {
                    const startDate = parseDate(field.value?.[0])
                    const endDate = parseDate(field.value?.[1])

                    return (
                        <Popover.Root>
                            <Popover.Trigger asChild disabled={disabled}>
                                <input
                                    id={fieldName}
                                    type="text"
                                    readOnly
                                    disabled={disabled}
                                    value={Array.isArray(field.value) ? field.value.join(' - ') : ''}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                                            e.preventDefault()
                                            e.currentTarget.click()
                                        }
                                    }}
                                />
                            </Popover.Trigger>
                            <Popover.Anchor />
                            <Popover.Portal>
                                <Popover.Content className="form-date-range--popover">
                                    <DateRangePicker
                                        months={2}
                                        direction="horizontal"
                                        minDate={rowField.minDate}
                                        locale={cs}
                                        showMonthAndYearPickers={false}
                                        ranges={[
                                            {
                                                startDate: startDate ?? new Date(),
                                                endDate: endDate ?? startDate ?? new Date(),
                                            },
                                        ]}
                                        staticRanges={[]}
                                        inputRanges={[]}
                                        onPreviewChange={() => {}}
                                        weekdayDisplayFormat="EEEEE"
                                        monthDisplayFormat="LLLL"
                                        onChange={e => {
                                            const changeTime = [
                                                format(e.range1.startDate ?? new Date(), DATE_FORMAT),
                                                format(e.range1.endDate ?? new Date(), DATE_FORMAT),
                                            ]

                                            field.onChange(changeTime)
                                            rowField.onChange?.(changeTime)
                                        }}
                                    />
                                </Popover.Content>
                            </Popover.Portal>
                        </Popover.Root>
                    )
                }}
            />
        </FieldRow>
    )
}

export default DateRangeField
